import { DateTime } from 'luxon';

/**
 * Business timezone for all session datetime display and capture.
 * Backend stores UTC; this module converts for presentation only.
 * America/Tijuana = PST (UTC-8) / PDT (UTC-7, follows US Pacific DST).
 */
export const SESSION_TZ = 'America/Tijuana';

/**
 * Compose a local date (YYYY-MM-DD) and a local time (HH:mm) into a UTC ISO string
 * suitable to send to the backend.
 */
export const localToUtcIso = (localDate: string, localTime: string): string | null => {
  if (!localDate || !localTime) return null;
  const [hours, minutes] = localTime.split(':').map(Number);
  const [year, month, day] = localDate.split('-').map(Number);
  if ([year, month, day, hours, minutes].some(n => Number.isNaN(n))) return null;
  const dt = DateTime.fromObject(
    { year, month, day, hour: hours, minute: minutes, second: 0, millisecond: 0 },
    { zone: SESSION_TZ }
  );
  if (!dt.isValid) return null;
  return dt.toUTC().toISO();
};

/**
 * Parse a server ISO string (UTC) and split it into local date + time parts
 * (`{ date: 'YYYY-MM-DD', time: 'HH:mm' }`) for prefilling form inputs.
 */
export const utcIsoToLocalParts = (iso: string | null | undefined): { date: string; time: string } => {
  if (!iso) return { date: '', time: '' };
  const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(SESSION_TZ);
  if (!dt.isValid) return { date: '', time: '' };
  return { date: dt.toFormat('yyyy-LL-dd'), time: dt.toFormat('HH:mm') };
};

/**
 * Human-friendly date for tables/cards. Example: "08 ene 2026".
 */
export const formatSessionDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(SESSION_TZ);
  if (!dt.isValid) return '—';
  return dt.setLocale('es-MX').toFormat('dd LLL yyyy');
};

/**
 * Human-friendly time. Example: "08:00".
 */
export const formatSessionTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(SESSION_TZ);
  if (!dt.isValid) return '—';
  return dt.toFormat('HH:mm');
};

/**
 * Human-friendly date+time. Example: "08 ene 2026 · 08:00".
 */
export const formatSessionDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(SESSION_TZ);
  if (!dt.isValid) return '—';
  return dt.setLocale('es-MX').toFormat("dd LLL yyyy · HH:mm");
};

/**
 * Duration in minutes between two UTC ISO strings.
 */
export const durationMinutes = (
  startIso: string | null | undefined,
  endIso: string | null | undefined
): number | null => {
  if (!startIso || !endIso) return null;
  const start = DateTime.fromISO(startIso, { zone: 'utc' });
  const end = DateTime.fromISO(endIso, { zone: 'utc' });
  if (!start.isValid || !end.isValid) return null;
  return Math.round(end.diff(start, 'minutes').minutes);
};

/**
 * Report filter helper: build `startDate` / `endDate` query strings in
 * YYYY-MM-DD format. The backend adjusts endDate to end-of-day automatically.
 */
export const periodRangeIso = (preset: 'lastMonth' | 'last3Months' | 'last6Months' | 'lastYear' | 'all'): {
  startDate: string | null;
  endDate: string | null;
} => {
  if (preset === 'all') return { startDate: null, endDate: null };
  const now = DateTime.now().setZone(SESSION_TZ);
  const endDate = now.toFormat('yyyy-LL-dd');
  const startMap: Record<typeof preset, DateTime> = {
    lastMonth: now.minus({ months: 1 }).startOf('day'),
    last3Months: now.minus({ months: 3 }).startOf('day'),
    last6Months: now.minus({ months: 6 }).startOf('day'),
    lastYear: now.minus({ years: 1 }).startOf('day'),
    all: now,
  } as Record<typeof preset, DateTime>;
  return {
    startDate: startMap[preset].toFormat('yyyy-LL-dd'),
    endDate,
  };
};
